// ============================================================================
// STRICT DATA GRID — Strict, opinionated data grid for NON-work-items modules
//
// The generic counterpart to StrictModuleHeader: one component that owns the
// entire "list surface" so every module list looks identical:
//
//   [search ▶ filter pills/dropdowns ▶ Filters toggle ▶ toolbar slot ▶ columns]
//   ────────────────────────────────────────────────────────────────────────
//   sortable header row
//   rows (click / select / expand / row actions)
//   ────────────────────────────────────────────────────────────────────────
//   Showing X - Y of Z · page-size · First ‹ Page N of M › Last
//
// Visual language is copied 1:1 from WorkItemsDataGrid's 'default' variant
// (gray-50 head, uppercase 11px header cells, px-3 py-2 cells, identical
// pagination footer) so a StrictDataGrid is indistinguishable from the
// work-items list to a user.
//
// TWO MODES (+ HYBRID SEARCH)
//   • Client mode (default): pass `rows` + `searchAccessor` + filters with
//     `accessor`s — the grid applies search/filters/sort/pagination itself.
//   • Server mode: set `serverPagination` (and/or `manual`). The grid renders
//     the same controls but does NOT touch `rows` — wire `searchValue` /
//     filter `selected`/`onChange` / `onSortChange` to your fetch.
//   • Hybrid search (WorkItemsFilterBar's dual search): `searchAccessor` +
//     controlled `searchValue`/`onSearchChange` + `searchedQuery` (the query
//     that produced `rows`). While `searchedQuery !== search` the grid is
//     "pending" — the accessor filters current rows for INSTANT feedback and
//     the search box spins. Once they match, server rows are authoritative
//     and the accessor is skipped (the backend may match fields the accessor
//     can't see — e.g. comments, blobs).
//
// USAGE
//   <StrictDataGrid
//     rows={devices}
//     getRowId={(d) => d.id}
//     storageKey="iam_devices"
//     searchAccessor={(d) => [d.deviceName, d.ownerName, d.wireguardIp]}
//     searchPlaceholder="Search devices..."
//     filters={[{ id: 'platform', label: 'Platform', options, accessor: (d) => d.platform }]}
//     columns={[{ key: 'name', label: 'Device', sortable: true,
//                 sortValue: (d) => d.deviceName, render: (d) => ... }]}
//     onRowClick={(d) => openDetail(d)}
//   />
//
// Date: October 2026
// ============================================================================

'use client';

import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Search, X, ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
  CheckSquare, Square, Columns3, Check, Filter, MoreHorizontal,
  AlertTriangle, Trash2, Loader2, type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';
import { SectionLoader } from '@/components/shared/BrandedLoadingSpinner';

// ============================================================================
// TYPES
// ============================================================================

export type StrictGridRowId = string | number;
export type StrictGridSortDirection = 'asc' | 'desc';

export interface StrictGridSort {
  key: string;
  direction: StrictGridSortDirection;
}

export interface StrictDataGridColumn<T> {
  key: string;
  label: React.ReactNode;
  /** Tailwind width class on the header cell, e.g. 'w-40'. */
  width?: string;
  align?: 'left' | 'center' | 'right';
  /** Header click toggles sort. Provide `sortValue` for correct ordering —
   *  without it the rendered cell's text content is not used (no sort). */
  sortable?: boolean;
  sortValue?: (row: T) => string | number | null | undefined;
  render: (row: T) => React.ReactNode;
  /** Hide this column on the mobile card layout. */
  mobileHidden?: boolean;
  /** Shorter label for the mobile card label/value pairs. */
  mobileLabel?: string;
  /** Column starts hidden (still listed in the Columns settings popover). */
  defaultVisible?: boolean;
  /** Set false to pin the column — excluded from the Columns popover. */
  toggleable?: boolean;
}

export interface StrictGridFilterOption {
  value: string;
  label: string;
  /** Optional status dot class, e.g. 'bg-emerald-500'. */
  color?: string;
}

export interface StrictDataGridFilter<T> {
  id: string;
  label: string;
  icon?: LucideIcon;
  options: StrictGridFilterOption[];
  /**
   * 'dropdown' (default) — multi-select dropdown with checkmarks.
   * 'pills'    — single-select segmented pill group (adds an implicit
   *              leading "All" pill that clears the selection).
   */
  variant?: 'dropdown' | 'pills';
  /**
   * Extract the row's filterable value(s). When provided, the grid applies
   * the filter client-side. Omit for server-side/external filtering — the
   * control still renders and reports via `onChange`.
   */
  accessor?: (row: T) => string | number | Array<string | number> | null | undefined;
  /** Label for the leading "All" pill (pills variant). Default 'All'. */
  allLabel?: string;
  /** Pills variant: set false to hide the leading "All" pill — selection is
   *  then always exactly one option (e.g. a lookback-window picker). */
  showAll?: boolean;
  /** Controlled selection. Pills read `selected[0]`; `[]`/`['']` = All. */
  selected?: string[];
  onChange?: (selected: string[]) => void;
}

export interface StrictGridRowAction {
  id: string;
  label: string;
  icon?: LucideIcon;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
  title?: string;
}

export interface StrictGridServerPagination {
  /** 1-based page index. */
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  /** Known row total → renders "Page X of Y". Omit for cursor-style APIs. */
  total?: number;
  /** Required when `total` is unknown — enables the Next/Last controls. */
  hasMore?: boolean;
}

// ---------------------------------------------------------------------------
// Strictness — mirrors StrictModuleHeader's strictCards() model.
// In dev: hard violations THROW (fail loudly so the inconsistency is fixed
// immediately). Soft violations console.error. In production everything is a
// no-op — the render layer degrades gracefully instead of crashing.
// ---------------------------------------------------------------------------

function assertStrictGrid<T>(props: {
  columns?: StrictDataGridColumn<T>[];
  storageKey?: string;
  searchAccessor?: StrictDataGridProps<T>['searchAccessor'];
  searchValue?: string;
  onSearchChange?: (v: string) => void;
  searchedQuery?: string;
  filters?: StrictDataGridFilter<T>[];
  selectable?: boolean;
  renderBulkActions?: unknown;
  onSelectionChange?: unknown;
  expandedKey?: StrictGridRowId | null;
  onExpandedChange?: unknown;
  hidePagination?: boolean;
  serverPagination?: unknown;
  loadingMessage?: string;
  emptyState?: unknown;
  pageSizeOptions?: number[];
}): void {
  if (process.env.NODE_ENV === 'production') return;

  const hard = (msg: string): never => {
    throw new Error(`[StrictDataGrid] ${msg}`);
  };

  // --- Hard violations (broken/contradictory config) ---
  if (!props.columns || props.columns.length === 0) {
    hard('`columns` must contain at least one column.');
  }
  const seen = new Set<string>();
  for (const c of props.columns ?? []) {
    if (seen.has(c.key)) hard(`Duplicate column key "${c.key}" — column keys must be unique.`);
    seen.add(c.key);
  }
  if (props.hidePagination && props.serverPagination) {
    hard('`hidePagination` and `serverPagination` are contradictory — hidePagination is for short client-side lists only.');
  }
  if ((props.searchValue === undefined) !== (props.onSearchChange === undefined)) {
    hard('`searchValue` and `onSearchChange` must be provided together (controlled search pair).');
  }
  if (!props.searchAccessor && !props.onSearchChange) {
    hard('Search is mandatory — pass `searchAccessor` (client mode) or `onSearchChange` (server-side search). The search slot always renders.');
  }
  if (props.searchedQuery !== undefined && (!props.onSearchChange || !props.searchAccessor)) {
    hard('`searchedQuery` is the hybrid-search sync marker — it requires `searchAccessor` + `onSearchChange` together.');
  }
  if (props.selectable && !props.renderBulkActions && !props.onSelectionChange) {
    hard('`selectable` requires `renderBulkActions` or `onSelectionChange` — selection with no outcome is dead UI.');
  }
  for (const f of props.filters ?? []) {
    if (!f.accessor && f.selected === undefined && !f.onChange) {
      hard(`Filter "${f.id}" has no \`accessor\`, \`selected\`, or \`onChange\` — it cannot affect the rows.`);
    }
  }
  if (props.expandedKey !== undefined && !props.onExpandedChange) {
    hard('`expandedKey` is controlled — pass `onExpandedChange` too, or drop both for internal expansion.');
  }

}

/** Soft consistency checks — run once per mount (console.error, dev only). */
function warnStrictGrid<T>(props: Parameters<typeof assertStrictGrid<T>>[0]): void {
  if (process.env.NODE_ENV === 'production') return;
  const soft = (msg: string): void => {
    console.error(`[StrictDataGrid] ${msg}`);
  };
  if (!props.storageKey && (props.columns ?? []).some((c) => c.toggleable !== false)) {
    soft('No `storageKey` — the Columns popover renders but visibility is not persisted. Pass a unique storageKey for consistency.');
  }
  if (!props.emptyState) {
    soft('No `emptyState` — the generic empty state will render. Provide icon/title/description matching the surface.');
  }
  if (!props.loadingMessage) {
    soft('No `loadingMessage` — the generic "Loading..." will render. Provide one matching the surface.');
  }
  if (props.pageSizeOptions && props.pageSizeOptions.some((n) => ![10, 25, 50, 100, 200].includes(n))) {
    soft(`\`pageSizeOptions\` should stay within [10, 25, 50, 100, 200] — got [${props.pageSizeOptions.join(', ')}].`);
  }
}

// ---------------------------------------------------------------------------
// "+ New" convention — the create action NEVER lives in the grid toolbar.
// A grid declares `newAction` and the registration context (provided by
// ModulePageShell/SectionShell) renders it as the StrictModuleHeader "+ New"
// button in Row A — same position on every page.
// ---------------------------------------------------------------------------

export interface StrictGridNewAction {
  label: string;
  icon?: LucideIcon;
  onClick: () => void;
  dataTourId?: string;
}

export const StrictNewActionContext = React.createContext<{
  register: (action: StrictGridNewAction) => () => void;
} | null>(null);

/** Register a page-level "+ New" action on the nearest StrictModuleHeader.
 *  For components that own their create flow without a StrictDataGrid
 *  (e.g. enrollment config cards, geofence cards). */
export function useStrictNewAction(action: StrictGridNewAction | undefined): void {
  const ctx = useContext(StrictNewActionContext);
  useEffect(() => {
    if (!action || !ctx) return;
    return ctx.register(action);
  }, [ctx, action]);
}

export interface StrictDataGridProps<T> {
  rows: T[];
  getRowId: (row: T) => StrictGridRowId;
  columns: StrictDataGridColumn<T>[];

  // ---- Persistence (column visibility) ----
  /** Persists Columns-popover visibility choices. The popover always
   *  renders when columns are toggleable — omitting storageKey only disables
   *  persistence (dev console errors). */
  storageKey?: string;

  // ---- Search ----
  /** Fields searched client-side. Required unless `onSearchChange` is set
   *  (server-side search) — the search input always renders. */
  searchAccessor?: (row: T) => Array<string | number | null | undefined>;
  searchPlaceholder?: string;
  /** data-tour-id applied to the built-in search input. */
  searchTourId?: string;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  /** Hybrid search — the query string that produced `rows` (set it in the same
   *  update as your server response). Requires `searchAccessor` + the
   *  controlled `searchValue`/`onSearchChange` pair. While
   *  `searchedQuery !== search` the grid is pending: it applies
   *  `searchAccessor` to the current rows for instant feedback and shows a
   *  spinner in the search box. Once they match, server results are
   *  authoritative — the accessor is NOT re-applied. Omit to keep the
   *  accessor applied permanently (instant-filter + callback mode). */
  searchedQuery?: string;
  /** Search is ALWAYS rendered — the universal left toolbar slot. Server-mode
   *  grids wire it via `onSearchChange`; client-mode via `searchAccessor`. */

  // ---- Declarative filters ----
  filters?: StrictDataGridFilter<T>[];

  // ---- Deleted rows ----
  /** Marks a row as deleted (uninstalled/soft-deleted). When ANY row is
   *  deleted, a "Deleted" pill appears in the filters cluster — deleted rows
   *  are hidden by default and only shown while the pill is active. */
  deletedAccessor?: (row: T) => boolean;
  /** Label for the built-in deleted pill. Default "Deleted". */
  deletedLabel?: string;

  // ---- Create action ----
  /** "+ New" button — rendered on the StrictModuleHeader (Row A), never in
   *  this grid's toolbar. Requires a StrictNewActionContext provider
   *  (ModulePageShell/SectionShell provide it). */
  newAction?: StrictGridNewAction;

  // ---- Advanced (collapsible) filter panel ----
  /** Custom filter UI toggled by a built-in "Filters (n)" button — for
   *  server-side filter panels (date ranges, free-text, dependent selects). */
  advancedFilters?: React.ReactNode;
  advancedFilterCount?: number;
  advancedFiltersDefaultOpen?: boolean;
  /** Controlled open-state for the advanced panel (e.g. a deep-link that
   *  must re-open the panel after mount). */
  advancedFiltersOpen?: boolean;
  onAdvancedFiltersOpenChange?: (open: boolean) => void;

  // ---- Panel header (two-panel / split layouts) ----
  /** Optional header strip rendered above the controls bar — use when
   *  placing multiple grids side-by-side inside a `grid grid-cols-1 gap-4
   *  lg:grid-cols-2` wrapper so every panel has the same title block. */
  panelTitle?: React.ReactNode;
  panelSubtitle?: React.ReactNode;

  // ---- Toolbar ----
  /** Extra controls on the right of the controls bar (export, refresh, New).
   *  Use <StrictGridToolbarButton> for the matching compact style. */
  toolbarSlot?: React.ReactNode;
  /** Left-of-search content (e.g. a section description or count text). */
  toolbarLeading?: React.ReactNode;

  // ---- Sorting ----
  defaultSort?: StrictGridSort;
  /** Server-side sorting: fires instead of sorting rows client-side. */
  onSortChange?: (sort: StrictGridSort | null) => void;

  // ---- Selection ----
  selectable?: boolean;
  onSelectionChange?: (selectedIds: Set<StrictGridRowId>, selectedRows: T[]) => void;
  /** Rendered in the controls bar when selection is non-empty (bulk ops). */
  renderBulkActions?: (selectedRows: T[], clearSelection: () => void) => React.ReactNode;

  // ---- Row interactions ----
  onRowClick?: (row: T) => void;
  /** Trailing actions column. ≤2 render as icon buttons; 3+ collapse to a ⋯ menu. */
  rowActions?: (row: T) => StrictGridRowAction[];
  /** Expand-on-click detail panel rendered full-width under the row. When
   *  set, row click toggles expansion (onRowClick is not fired). */
  renderExpandedRow?: (row: T) => React.ReactNode;
  /** Controlled expanded row id — use with onExpandedChange when the panel
   *  needs lazy data fetches or external collapse. */
  expandedKey?: StrictGridRowId | null;
  onExpandedChange?: (id: StrictGridRowId | null) => void;
  /** Extra row className, e.g. a deleted/danger tint. */
  rowClassName?: (row: T) => string | undefined;

  // ---- Pagination ----
  /** Client mode: rows/page options. Default [50, 100, 200], initial 50. */
  pageSizeOptions?: number[];
  defaultPageSize?: number;
  /** Server mode: parent fetches one page at a time. Implies `manual`. */
  serverPagination?: StrictGridServerPagination;
  /** Skip ALL client-side row processing (search/filter/sort). Implied by
   *  serverPagination. */
  manual?: boolean;
  /** Hide the pagination footer — for short fixed-size lists (top-10 panels,
   *  side-by-side grids) where paging controls are noise. */
  hidePagination?: boolean;

  // ---- States ----
  loading?: boolean;
  loadingMessage?: string;
  emptyState?: {
    icon?: LucideIcon;
    title: string;
    description?: string;
    ctaLabel?: string;
    onCtaClick?: () => void;
  };

  // ---- Mobile ----
  /** Custom card content for the mobile list (grid still wraps it in the
   *  card chrome + selection/click handling). Default: label:value pairs. */
  renderMobileCard?: (row: T) => React.ReactNode;

  // ---- Misc ----
  primaryColor?: string;
  dataTourId?: string;
  className?: string;
}

// ============================================================================
// THEME — copied from WorkItemsDataGrid 'default' styleVariant (keep in sync)
// ============================================================================

export const GRID = {
  container:
    'max-w-full min-w-0 overflow-hidden rounded-lg border border-gray-200 bg-white/80 shadow-sm dark:border-gray-700 dark:bg-gray-900/30',
  controlsBar:
    'flex flex-wrap items-center gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2 sm:px-4 dark:border-gray-700 dark:bg-gray-900',
  tableWrapper:
    'max-w-full min-w-0 overflow-x-auto overscroll-x-contain pb-2.5 scrollbar-thin scrollbar-thumb-gray-400/80 scrollbar-track-transparent dark:scrollbar-thumb-gray-500 dark:scrollbar-track-transparent',
  table: 'min-w-max w-full divide-y divide-gray-200 dark:divide-gray-700',
  thead: 'bg-gray-50 dark:bg-gray-900',
  th: 'px-3 py-2.5 text-[11px] font-semibold uppercase tracking-[0.18em] text-gray-500 dark:text-gray-300',
  tbody: 'bg-white divide-y divide-gray-100 dark:bg-gray-900 dark:divide-gray-800',
  tr: 'hover:bg-gray-50 transition-colors cursor-pointer dark:hover:bg-gray-800/50',
  td: 'px-3 py-2',
  pagination:
    'flex flex-col items-stretch justify-between gap-2 border-t border-gray-200 bg-gray-50 px-2 py-2.5 sm:flex-row sm:items-center sm:gap-0 sm:px-3 dark:border-gray-700 dark:bg-gray-900',
  pageSizeSelect:
    'touch-manipulation rounded-md border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 sm:text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300',
  pageButton:
    'touch-manipulation rounded-md border border-gray-200 bg-gray-100 text-gray-700 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300',
  controlButton:
    'inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-600 shadow-sm transition-colors hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700',
  pillGroup:
    'inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white p-1 text-xs shadow-sm dark:border-gray-700 dark:bg-gray-800',
  selectedRow: 'bg-blue-50 dark:bg-blue-900/20',
  mobileList: 'divide-y divide-gray-100 dark:divide-gray-800',
  mobileHeader: 'flex items-center justify-between bg-gray-50 px-3 py-1.5 dark:bg-gray-900',
  mobileCard: 'p-3 transition-colors active:bg-gray-100 dark:active:bg-gray-700',
};

const COLUMN_VIS_KEY_PREFIX = 'ta_strict_grid_cols::';
const ROW_ACTIONS_INLINE_MAX = 2;

// ============================================================================
// SMALL EXPORTED PIECES
// ============================================================================

/** Compact toolbar button matching the grid controls-bar style. */
export function StrictGridToolbarButton({
  icon: Icon,
  label,
  primary,
  loading,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  icon?: LucideIcon;
  label?: string;
  primary?: boolean;
  loading?: boolean;
}) {
  return (
    <button
      type="button"
      {...props}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium shadow-sm transition-colors disabled:opacity-50',
        primary
          ? 'bg-blue-600 text-white hover:bg-blue-700'
          : 'border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700',
        className,
      )}
    >
      {Icon && <Icon className={cn('h-3.5 w-3.5', loading && 'animate-spin')} />}
      {label}
    </button>
  );
}

export type StrictGridBadgeTone =
  | 'gray' | 'blue' | 'green' | 'amber' | 'red' | 'purple' | 'indigo' | 'sky' | 'teal' | 'orange';

const BADGE_TONE_CLASS: Record<StrictGridBadgeTone, string> = {
  gray: 'bg-gray-100 text-gray-600 dark:bg-gray-700/50 dark:text-gray-300',
  blue: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  green: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  amber: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  purple: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  indigo: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  sky: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300',
  teal: 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
  orange: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
};

/** Standard status pill — the `rounded-full px-2 py-0.5 text-xs` badge every
 *  section was hand-rolling. */
export function StrictGridBadge({
  tone = 'gray',
  className,
  title,
  children,
}: {
  tone?: StrictGridBadgeTone;
  className?: string;
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <span title={title} className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium', BADGE_TONE_CLASS[tone], className)}>
      {children}
    </span>
  );
}

// ============================================================================
// INTERNALS
// ============================================================================

function readStoredColumns(storageKey: string | undefined): string[] | null {
  if (!storageKey || typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(COLUMN_VIS_KEY_PREFIX + storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : null;
  } catch {
    return null;
  }
}

function writeStoredColumns(storageKey: string, keys: string[]) {
  if (typeof window === 'undefined') return;
  localStorage.setItem(COLUMN_VIS_KEY_PREFIX + storageKey, JSON.stringify(keys));
}

function compareSortValues(a: string | number | null | undefined, b: string | number | null | undefined): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}

/** Portal dropdown (the controls bar sits inside an overflow-hidden card, so
 *  menus must escape via fixed positioning — same approach as
 *  WorkItemsFilterBar). */
export function GridDropdown({
  trigger,
  children,
  isOpen,
  onClose,
  align = 'left',
  width = 'w-56',
}: {
  trigger: React.ReactNode;
  children: React.ReactNode;
  isOpen: boolean;
  onClose: () => void;
  align?: 'left' | 'right';
  width?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<React.CSSProperties>({ top: 0, left: 0 });

  useEffect(() => {
    if (!isOpen) return;
    const update = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect || typeof window === 'undefined') return;
      const menuWidth = menuRef.current?.offsetWidth || 224;
      const left = align === 'right'
        ? Math.max(8, rect.right - menuWidth)
        : Math.min(rect.left, Math.max(8, window.innerWidth - menuWidth - 8));
      setStyle({ top: rect.bottom + 4, left, zIndex: 100 });
    };
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (containerRef.current?.contains(t) || menuRef.current?.contains(t)) return;
      onClose();
    };
    const frame = window.requestAnimationFrame(update);
    document.addEventListener('mousedown', onDown);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [align, isOpen, onClose]);

  return (
    <div ref={containerRef} className="relative inline-flex max-w-full align-middle">
      <div ref={triggerRef} className="inline-flex max-w-full">
        {trigger}
      </div>
      {isOpen && typeof document !== 'undefined'
        ? createPortal(
          <div
            ref={menuRef}
            style={style}
            className={cn('fixed rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800', width)}
          >
            {children}
          </div>,
          document.body,
        )
        : null}
    </div>
  );
}

// ============================================================================
// COMPONENT
// ============================================================================

export default function StrictDataGrid<T>({
  rows,
  getRowId,
  columns,
  storageKey,
  searchAccessor,
  searchPlaceholder = 'Search...',
  searchTourId,
  searchValue,
  onSearchChange,
  searchedQuery,
  filters = [],
  deletedAccessor,
  deletedLabel = 'Deleted',
  newAction,
  advancedFilters,
  advancedFilterCount = 0,
  advancedFiltersDefaultOpen = false,
  advancedFiltersOpen,
  onAdvancedFiltersOpenChange,
  panelTitle,
  panelSubtitle,
  toolbarSlot,
  toolbarLeading,
  defaultSort,
  onSortChange,
  selectable = false,
  onSelectionChange,
  renderBulkActions,
  onRowClick,
  rowActions,
  renderExpandedRow,
  expandedKey,
  onExpandedChange,
  rowClassName,
  pageSizeOptions = [50, 100, 200],
  defaultPageSize = 50,
  serverPagination,
  manual = false,
  hidePagination = false,
  loading = false,
  loadingMessage = 'Loading...',
  emptyState,
  renderMobileCard,
  primaryColor = '#2563eb',
  dataTourId,
  className,
}: StrictDataGridProps<T>) {
  assertStrictGrid<T>({
    columns, storageKey, searchAccessor, searchValue, onSearchChange,
    searchedQuery, filters, selectable, renderBulkActions, onSelectionChange,
    expandedKey, onExpandedChange, serverPagination, hidePagination,
    loadingMessage, emptyState, pageSizeOptions,
  });

  // "+ New" registration — the header renders it; dev error if no provider.
  const newActionCtx = useContext(StrictNewActionContext);
  useEffect(() => {
    if (!newAction) return;
    if (!newActionCtx) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('[StrictDataGrid] `newAction` has no StrictNewActionContext provider — wrap the page in ModulePageShell/SectionShell so the + New button renders on the header.');
      }
      return;
    }
    return newActionCtx.register(newAction);
  }, [newActionCtx, newAction]);
  // newAction should be memoized by the consumer (useCallback/useMemo).

  // Soft checks — once per mount only (dev).
  useEffect(() => {
    warnStrictGrid<T>({
      columns, storageKey, loadingMessage, emptyState, pageSizeOptions,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isMobile = useIsMobileViewport();
  const isManual = manual || Boolean(serverPagination);

  // ---------- Dev-time strictness ----------
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    if (!columns.length) {
      console.error('[StrictDataGrid] columns must not be empty.');
    }
    const keys = new Set<string>();
    for (const col of columns) {
      if (keys.has(col.key)) {
        console.error(`[StrictDataGrid] duplicate column key "${col.key}".`);
      }
      keys.add(col.key);
      if (col.sortable && !col.sortValue && !onSortChange) {
        console.warn(
          `[StrictDataGrid] column "${col.key}" is sortable but has no sortValue ` +
          `(and no onSortChange) — it will sort by row order, which is a no-op.`,
        );
      }
    }
  }, [columns, onSortChange]);

  // ---------- Search ----------
  const [internalSearch, setInternalSearch] = useState('');
  const search = searchValue ?? internalSearch;
  const setSearch = useCallback(
    (v: string) => {
      if (searchValue === undefined) setInternalSearch(v);
      onSearchChange?.(v);
    },
    [searchValue, onSearchChange],
  );

  // Hybrid search (WorkItemsFilterBar contract): when BOTH `searchAccessor`
  // and `onSearchChange` are provided, the grid is "pending" while the live
  // input differs from `searchedQuery` (the query that produced `rows`).
  // Pending → accessor filters current rows for instant feedback + spinner.
  // Settled → server rows are authoritative; the accessor is skipped so
  // backend-only matches (fields the accessor can't see) still show.
  const hybridSearch = Boolean(onSearchChange && searchAccessor);
  const searchPending = hybridSearch && (searchedQuery ?? '') !== search.trim();

  // ---------- Filters ----------
  const [internalFilterSel, setInternalFilterSel] = useState<Record<string, string[]>>({});
  const filterSelection = useCallback(
    (f: StrictDataGridFilter<T>): string[] => f.selected ?? internalFilterSel[f.id] ?? [],
    [internalFilterSel],
  );
  const setFilterSelection = useCallback(
    (f: StrictDataGridFilter<T>, next: string[]) => {
      if (f.selected === undefined) {
        setInternalFilterSel((prev) => ({ ...prev, [f.id]: next }));
      }
      f.onChange?.(next);
    },
    [],
  );
  const [showDeleted, setShowDeleted] = useState(false);
  const [internalAdvancedOpen, setInternalAdvancedOpen] = useState(advancedFiltersDefaultOpen);
  const advancedOpen = advancedFiltersOpen ?? internalAdvancedOpen;
  const setAdvancedOpen = useCallback(
    (v: boolean | ((prev: boolean) => boolean)) => {
      const next = typeof v === 'function' ? v(advancedFiltersOpen ?? internalAdvancedOpen) : v;
      if (advancedFiltersOpen === undefined) setInternalAdvancedOpen(next);
      onAdvancedFiltersOpenChange?.(next);
    },
    [advancedFiltersOpen, internalAdvancedOpen, onAdvancedFiltersOpenChange],
  );

  // ---------- Column visibility ----------
  const toggleableColumns = useMemo(() => columns.filter((c) => c.toggleable !== false), [columns]);
  const [hiddenCols, setHiddenCols] = useState<Set<string>>(() => {
    const stored = readStoredColumns(storageKey);
    if (stored) {
      const valid = new Set(columns.map((c) => c.key));
      const hidden = new Set<string>();
      for (const c of columns) {
        if (c.toggleable === false) continue;
        if (!stored.includes(c.key)) hidden.add(c.key);
      }
      // Stored list must reference real columns only
      if (stored.every((k) => valid.has(k))) return hidden;
    }
    return new Set(columns.filter((c) => c.defaultVisible === false).map((c) => c.key));
  });
  const [colSettingsOpen, setColSettingsOpen] = useState(false);

  useEffect(() => {
    const stored = readStoredColumns(storageKey);
    if (stored) {
      const valid = new Set(columns.map((c) => c.key));
      if (stored.every((k) => valid.has(k))) {
        setHiddenCols(new Set(columns.filter((c) => c.toggleable !== false && !stored.includes(c.key)).map((c) => c.key)));
        return;
      }
    }
    setHiddenCols(new Set(columns.filter((c) => c.defaultVisible === false).map((c) => c.key)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storageKey]);

  const setColumnVisible = useCallback(
    (key: string, visible: boolean) => {
      setHiddenCols((prev) => {
        const next = new Set(prev);
        if (visible) next.delete(key);
        else next.add(key);
        if (storageKey) {
          const visibleKeys = columns.filter((c) => c.toggleable !== false && !next.has(c.key)).map((c) => c.key);
          writeStoredColumns(storageKey, visibleKeys);
        }
        return next;
      });
    },
    [columns, storageKey],
  );

  const visibleColumns = useMemo(() => columns.filter((c) => !hiddenCols.has(c.key)), [columns, hiddenCols]);

  // ---------- Sort ----------
  const [sort, setSort] = useState<StrictGridSort | null>(defaultSort ?? null);
  const handleSort = useCallback(
    (key: string) => {
      const next: StrictGridSort = {
        key,
        direction: sort?.key === key && sort.direction === 'asc' ? 'desc' : 'asc',
      };
      setSort(next);
      onSortChange?.(next);
    },
    [sort, onSortChange],
  );

  // ---------- Client pipeline: search → filters → sort → paginate ----------
  const processedRows = useMemo(() => {
    if (isManual) return rows;
    let list = rows;

    const q = search.trim().toLowerCase();
    if (q && searchAccessor && (!hybridSearch || searchPending)) {
      list = list.filter((row) =>
        searchAccessor(row).some((v) => v != null && String(v).toLowerCase().includes(q)),
      );
    }

    if (deletedAccessor) {
      list = list.filter((r) => (showDeleted ? deletedAccessor(r) : !deletedAccessor(r)));
    }
    for (const f of filters) {
      if (!f.accessor) continue;
      const sel = filterSelection(f).filter((v) => v !== '');
      if (sel.length === 0) continue;
      const selSet = new Set(sel.map(String));
      list = list.filter((row) => {
        const raw = f.accessor!(row);
        const vals = (Array.isArray(raw) ? raw : [raw]).filter((v) => v != null).map(String);
        return vals.some((v) => selSet.has(v));
      });
    }

    if (sort) {
      const col = columns.find((c) => c.key === sort.key);
      if (col?.sortValue) {
        const dir = sort.direction === 'asc' ? 1 : -1;
        list = [...list].sort((a, b) => dir * compareSortValues(col.sortValue!(a), col.sortValue!(b)));
      }
    }
    return list;
  }, [rows, isManual, search, searchAccessor, hybridSearch, searchPending, filters, filterSelection, sort, columns, deletedAccessor, showDeleted]);

  // ---------- Pagination ----------
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  const server = serverPagination;
  const effectivePage = server ? server.page : page;
  const effectivePageSize = server ? server.pageSize : pageSize;
  const totalRows = server ? (server.total ?? null) : processedRows.length;
  const totalPages = server
    ? (server.total != null ? Math.max(1, Math.ceil(server.total / server.pageSize)) : null)
    : Math.max(1, Math.ceil(processedRows.length / pageSize));

  const paginatedRows = useMemo(() => {
    if (server) return rows;
    const start = (page - 1) * pageSize;
    return processedRows.slice(start, start + pageSize);
  }, [server, rows, processedRows, page, pageSize]);

  // Reset/clamp client page when the pipeline output shrinks
  useEffect(() => {
    if (server) return;
    const maxPage = Math.max(1, Math.ceil(processedRows.length / pageSize));
    setPage((p) => (p > maxPage ? maxPage : p));
  }, [server, processedRows.length, pageSize]);

  // Filter/search changes reset to page 1
  const searchRef = useRef(search);
  useEffect(() => {
    if (searchRef.current !== search) {
      searchRef.current = search;
      setPage(1);
    }
  }, [search]);

  // ---------- Selection ----------
  const [selectedIds, setSelectedIds] = useState<Set<StrictGridRowId>>(new Set());
  const emitSelection = useCallback(
    (next: Set<StrictGridRowId>) => {
      setSelectedIds(next);
      onSelectionChange?.(next, rows.filter((r) => next.has(getRowId(r))));
    },
    [onSelectionChange, rows, getRowId],
  );
  const toggleRow = useCallback(
    (id: StrictGridRowId) => {
      const next = new Set(selectedIds);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      emitSelection(next);
    },
    [selectedIds, emitSelection],
  );
  const pageIds = paginatedRows.map(getRowId);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedIds.has(id));
  const somePageSelected = pageIds.some((id) => selectedIds.has(id));
  const toggleSelectAll = useCallback(() => {
    const next = new Set(selectedIds);
    if (allPageSelected) pageIds.forEach((id) => next.delete(id));
    else pageIds.forEach((id) => next.add(id));
    emitSelection(next);
  }, [selectedIds, allPageSelected, pageIds, emitSelection]);
  const clearSelection = useCallback(() => emitSelection(new Set()), [emitSelection]);
  const selectedRows = useMemo(() => rows.filter((r) => selectedIds.has(getRowId(r))), [rows, selectedIds, getRowId]);

  // ---------- Expansion ----------
  const [internalExpandedId, setInternalExpandedId] = useState<StrictGridRowId | null>(null);
  const expandedId = expandedKey !== undefined ? expandedKey : internalExpandedId;
  const expandable = Boolean(renderExpandedRow);
  const handleRowClick = useCallback(
    (row: T) => {
      if (expandable) {
        const id = getRowId(row);
        const next = expandedId === id ? null : id;
        if (expandedKey === undefined) setInternalExpandedId(next);
        onExpandedChange?.(next);
        return;
      }
      onRowClick?.(row);
    },
    [expandable, expandedId, expandedKey, getRowId, onExpandedChange, onRowClick],
  );

  // ---------- Derived ----------
  const hasToolbar =
    filters.length > 0 || advancedFilters || toolbarSlot || toolbarLeading ||
    toggleableColumns.length > 0;
  const deletedCount = deletedAccessor ? rows.filter(deletedAccessor).length : 0;
  const hasDeletedRows = deletedCount > 0;

  const totalColSpan =
    visibleColumns.length + (selectable ? 1 : 0) + (rowActions ? 1 : 0) + (expandable ? 1 : 0);

  const empty = emptyState ?? {
    icon: AlertTriangle,
    title: 'No records found',
    description: 'Adjust the search or filters to see more results.',
  };
  const EmptyIcon = empty.icon ?? AlertTriangle;

  const displayStart = paginatedRows.length === 0 ? 0 : (effectivePage - 1) * effectivePageSize + 1;
  const displayEnd = (effectivePage - 1) * effectivePageSize + paginatedRows.length;
  const canNext = server ? (server.hasMore ?? false) : effectivePage < (totalPages ?? 1);
  const canPrev = effectivePage > 1;

  const gotoPage = (p: number) => {
    if (server) server.onPageChange(p);
    else setPage(Math.min(Math.max(1, p), totalPages ?? 1));
  };
  const changePageSize = (size: number) => {
    if (server?.onPageSizeChange) server.onPageSizeChange(size);
    else {
      setPageSize(size);
      setPage(1);
    }
  };

  // ---------- Render helpers ----------
  const renderSortIcon = (col: StrictDataGridColumn<T>) => {
    if (!col.sortable) return null;
    if (sort?.key !== col.key) {
      return <ChevronUp className="h-3 w-3 text-gray-300 opacity-0 transition-opacity group-hover:opacity-60 dark:text-gray-600" />;
    }
    return sort.direction === 'asc'
      ? <ChevronUp className="h-3 w-3 text-blue-500" />
      : <ChevronDown className="h-3 w-3 text-blue-500" />;
  };

  const selectCheckbox = (checked: boolean, indeterminate: boolean, onClick: (e: React.MouseEvent) => void, size = 'w-4 h-4') => (
    <button type="button" onClick={onClick} className="rounded p-1 transition-colors hover:bg-gray-200 dark:hover:bg-gray-600">
      {checked ? (
        <CheckSquare className={cn(size, 'text-blue-600 dark:text-blue-400')} />
      ) : indeterminate ? (
        <span className={cn('flex items-center justify-center rounded border-2 border-blue-600 bg-blue-600 dark:border-blue-400 dark:bg-blue-400', size)}>
          <span className="h-0.5 w-2 bg-white" />
        </span>
      ) : (
        <Square className={cn(size, 'text-gray-400')} />
      )}
    </button>
  );

  const renderRowActionsCell = (row: T) => {
    const actions = (rowActions?.(row) ?? []).filter(Boolean);
    if (!actions.length) return null;
    if (actions.length <= ROW_ACTIONS_INLINE_MAX) {
      return (
        <div className="flex items-center justify-end gap-1">
          {actions.map((a) => {
            const AIcon = a.icon;
            return (
              <button
                key={a.id}
                type="button"
                title={a.title ?? a.label}
                aria-label={a.label}
                disabled={a.disabled}
                onClick={(e) => { e.stopPropagation(); a.onClick(); }}
                className={cn(
                  'rounded p-1.5 transition-colors disabled:opacity-40',
                  a.danger
                    ? 'text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30'
                    : 'text-gray-400 hover:bg-blue-50 hover:text-blue-600 dark:hover:bg-blue-900/30',
                )}
              >
                {AIcon && <AIcon className="h-4 w-4" />}
              </button>
            );
          })}
        </div>
      );
    }
    return <RowActionsMenu actions={actions} />;
  };

  // ---------- Controls bar ----------
  const renderControls = () => (
    <div className={GRID.controlsBar}>
      {toolbarLeading}
      {(
        <div className="relative min-w-[200px] flex-1 sm:max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            data-tour-id={searchTourId}
            className="w-full rounded-lg border border-gray-300 bg-white py-1.5 pl-9 pr-8 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
          />
          {searchPending && (
            <Loader2 className="absolute right-8 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-blue-500" />
          )}
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}

      {selectable && selectedIds.size > 0 && (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
          {selectedIds.size} selected
          <button type="button" onClick={clearSelection} className="rounded-full p-0.5 hover:bg-blue-100 dark:hover:bg-blue-800" aria-label="Clear selection">
            <X className="h-3 w-3" />
          </button>
        </span>
      )}
      {selectable && selectedIds.size > 0 && renderBulkActions?.(selectedRows, clearSelection)}

      <div className="ml-auto flex flex-wrap items-center gap-2">
        {filters.map((f) =>
          f.variant === 'pills' ? (
            <PillsFilter key={f.id} filter={f} selected={filterSelection(f)} onChange={(v) => { setPage(1); setFilterSelection(f, v); }} />
          ) : (
            <DropdownFilter key={f.id} filter={f} selected={filterSelection(f)} onChange={(v) => { setPage(1); setFilterSelection(f, v); }} />
          ),
        )}

        {deletedAccessor && hasDeletedRows && (
          <button
            type="button"
            onClick={() => setShowDeleted((v) => !v)}
            className={cn(
              GRID.controlButton,
              showDeleted && 'border-red-300 bg-red-50 text-red-700 dark:border-red-600 dark:bg-red-900/30 dark:text-red-300',
            )}
          >
            <Trash2 className="h-3.5 w-3.5" />
            {deletedLabel}
            <span className="rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-600 dark:bg-red-900/40 dark:text-red-300">
              {deletedCount}
            </span>
          </button>
        )}

        {advancedFilters && (
          <button
            type="button"
            onClick={() => setAdvancedOpen((v) => !v)}
            className={cn(
              GRID.controlButton,
              advancedOpen && 'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-600 dark:bg-blue-900/30 dark:text-blue-300',
            )}
          >
            <Filter className="h-3.5 w-3.5" />
            Filters
            {advancedFilterCount > 0 && (
              <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-blue-600 px-1 text-[10px] font-semibold text-white">
                {advancedFilterCount}
              </span>
            )}
          </button>
        )}

        {toolbarSlot}

        {toggleableColumns.length > 0 && (
          <GridDropdown
            align="right"
            isOpen={colSettingsOpen}
            onClose={() => setColSettingsOpen(false)}
            trigger={
              <button type="button" onClick={() => setColSettingsOpen((v) => !v)} className={GRID.controlButton}>
                <Columns3 className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Columns</span>
                {hiddenCols.size > 0 && (
                  <span className="rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-semibold text-gray-500 dark:bg-gray-700 dark:text-gray-300">
                    {visibleColumns.length}/{columns.length}
                  </span>
                )}
              </button>
            }
          >
            <div className="max-h-72 overflow-y-auto p-1">
              <div className="flex items-center justify-between border-b border-gray-100 px-2 py-1.5 dark:border-gray-700">
                <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">Visible columns</span>
                <button
                  type="button"
                  className="text-[11px] text-blue-600 hover:underline dark:text-blue-400"
                  onClick={() => {
                    const all = new Set<string>();
                    setHiddenCols(all);
                    if (storageKey) writeStoredColumns(storageKey, toggleableColumns.map((c) => c.key));
                  }}
                >
                  Show all
                </button>
              </div>
              {toggleableColumns.map((c) => (
                <label key={c.key} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-xs text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700">
                  <input
                    type="checkbox"
                    checked={!hiddenCols.has(c.key)}
                    onChange={(e) => setColumnVisible(c.key, e.target.checked)}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600"
                  />
                  <span className="truncate">{typeof c.label === 'string' ? c.label : c.key}</span>
                </label>
              ))}
            </div>
          </GridDropdown>
        )}
      </div>
    </div>
  );

  // ---------- Row renders ----------
  const renderDesktopRow = (row: T) => {
    const id = getRowId(row);
    const isExpanded = expandedId === id;
    return (
      <React.Fragment key={id}>
        <tr
          className={cn(GRID.tr, selectedIds.has(id) && GRID.selectedRow, rowClassName?.(row))}
          onClick={() => handleRowClick(row)}
        >
          {selectable && (
            <td className={cn(GRID.td, 'w-10 text-center')} onClick={(e) => e.stopPropagation()}>
              {selectCheckbox(selectedIds.has(id), false, () => toggleRow(id))}
            </td>
          )}
          {visibleColumns.map((col) => (
            <td
              key={col.key}
              className={cn(
                GRID.td,
                col.align === 'center' ? 'text-center' : col.align === 'right' ? 'text-right' : '',
              )}
            >
              {col.render(row)}
            </td>
          ))}
          {rowActions && (
            <td className={cn(GRID.td, 'w-24')} onClick={(e) => e.stopPropagation()}>
              {renderRowActionsCell(row)}
            </td>
          )}
          {expandable && (
            <td className={cn(GRID.td, 'w-8 text-center')}>
              <ChevronDown className={cn('inline h-4 w-4 text-gray-400 transition-transform', isExpanded && 'rotate-180')} />
            </td>
          )}
        </tr>
        {isExpanded && renderExpandedRow && (
          <tr>
            <td colSpan={totalColSpan} className="bg-gray-50 px-6 py-3 dark:bg-gray-800/40">
              {renderExpandedRow(row)}
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  const renderMobileCardDefault = (row: T) => {
    const shown = visibleColumns.filter((c) => !c.mobileHidden);
    const [first, ...rest] = shown;
    return (
      <div className="min-w-0 flex-1">
        {first && <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{first.render(row)}</div>}
        {rest.slice(0, 4).map((c) => (
          <div key={c.key} className="mt-1 flex items-baseline justify-between gap-3 text-xs">
            <span className="shrink-0 text-gray-400">{c.mobileLabel ?? (typeof c.label === 'string' ? c.label : c.key)}</span>
            <span className="min-w-0 truncate text-right text-gray-600 dark:text-gray-300">{c.render(row)}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderMobileRow = (row: T) => {
    const id = getRowId(row);
    const isExpanded = expandedId === id;
    return (
      <div key={id} className={cn(GRID.mobileCard, selectedIds.has(id) && GRID.selectedRow, rowClassName?.(row))}>
        <div className="flex items-start gap-3">
          {selectable && (
            <span onClick={(e) => e.stopPropagation()} className="mt-0.5">
              {selectCheckbox(selectedIds.has(id), false, () => toggleRow(id), 'w-5 h-5')}
            </span>
          )}
          <div className="min-w-0 flex-1" onClick={() => handleRowClick(row)}>
            {renderMobileCard ? renderMobileCard(row) : renderMobileCardDefault(row)}
          </div>
          {(rowActions || expandable) && (
            <span onClick={(e) => e.stopPropagation()} className="mt-0.5 shrink-0">
              {rowActions ? renderRowActionsCell(row) : (
                <ChevronDown className={cn('h-4 w-4 text-gray-400 transition-transform', isExpanded && 'rotate-180')} />
              )}
            </span>
          )}
        </div>
        {isExpanded && renderExpandedRow && (
          <div className="mt-2 rounded-lg bg-gray-50 p-3 dark:bg-gray-800/40" onClick={(e) => e.stopPropagation()}>
            {renderExpandedRow(row)}
          </div>
        )}
      </div>
    );
  };

  // ---------- Loading / empty ----------
  if (loading && paginatedRows.length === 0) {
    return (
      <div className={cn(GRID.container, className)} data-tour-id={dataTourId}>
        {hasToolbar && renderControls()}
        <div className="flex min-h-[min(50vh,28rem)] items-center justify-center p-8">
          <SectionLoader size="sm" primaryColor={primaryColor} message={loadingMessage} />
        </div>
      </div>
    );
  }

  const isEmpty = paginatedRows.length === 0;

  return (
    <div className={cn(GRID.container, className)} data-tour-id={dataTourId}>
      {(panelTitle || panelSubtitle) && (
        <div className="border-b border-gray-200 bg-gray-50 px-4 py-2 dark:border-gray-700 dark:bg-gray-800">
          {panelTitle && (
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200">{panelTitle}</h3>
          )}
          {panelSubtitle && (
            <p className="mt-0.5 text-[11px] text-gray-400">{panelSubtitle}</p>
          )}
        </div>
      )}

      {hasToolbar && renderControls()}

      {advancedFilters && advancedOpen && (
        <div className="border-b border-gray-200 bg-gray-50/60 px-3 py-3 sm:px-4 dark:border-gray-700 dark:bg-gray-900/40">
          {advancedFilters}
        </div>
      )}

      {isEmpty ? (
        <div className="flex min-h-[16rem] items-center justify-center px-6 py-10">
          <div className="max-w-sm text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-gray-200 bg-gray-50 text-gray-400 dark:border-gray-700 dark:bg-gray-800">
              <EmptyIcon className="h-6 w-6" />
            </div>
            <p className="text-sm font-semibold text-gray-900 dark:text-white">{empty.title}</p>
            {empty.description && <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{empty.description}</p>}
            {empty.ctaLabel && empty.onCtaClick && (
              <button
                type="button"
                onClick={empty.onCtaClick}
                className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-gray-900 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-gray-700 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-200"
              >
                {empty.ctaLabel}
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className={GRID.tableWrapper}>
          {isMobile ? (
            <div className={GRID.mobileList}>
              {selectable && (
                <div className={GRID.mobileHeader}>
                  <span className="flex items-center gap-2">
                    {selectCheckbox(allPageSelected, !allPageSelected && somePageSelected, toggleSelectAll)}
                    <span className="text-xs text-gray-600 dark:text-gray-300">Select all</span>
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-300">{paginatedRows.length} items</span>
                </div>
              )}
              {paginatedRows.map(renderMobileRow)}
            </div>
          ) : (
            <table className={GRID.table}>
              <thead className={GRID.thead}>
                <tr>
                  {selectable && (
                    <th className={cn(GRID.th, 'w-10 text-center')}>
                      {selectCheckbox(allPageSelected, !allPageSelected && somePageSelected, toggleSelectAll)}
                    </th>
                  )}
                  {visibleColumns.map((col) => (
                    <th
                      key={col.key}
                      className={cn(
                        GRID.th,
                        col.width,
                        col.align === 'center' ? 'text-center' : col.align === 'right' ? 'text-right' : 'text-left',
                        col.sortable && 'group cursor-pointer select-none hover:bg-gray-100 dark:hover:bg-gray-700',
                      )}
                      onClick={() => col.sortable && handleSort(col.key)}
                    >
                      <div className={cn(
                        'flex items-center gap-1.5',
                        col.align === 'center' && 'justify-center',
                        col.align === 'right' && 'justify-end',
                      )}>
                        <span>{col.label}</span>
                        {renderSortIcon(col)}
                      </div>
                    </th>
                  ))}
                  {rowActions && <th className={cn(GRID.th, 'w-24 text-right')} />}
                  {expandable && <th className={cn(GRID.th, 'w-8')} />}
                </tr>
              </thead>
              <tbody className={GRID.tbody}>
                {paginatedRows.map(renderDesktopRow)}
              </tbody>
            </table>
          )}

          {/* Pagination footer — identical layout to WorkItemsDataGrid */}
          {!hidePagination && (
          <div className={GRID.pagination}>
            <div className="flex flex-col items-center gap-2 sm:flex-row sm:gap-4">
              <span className="whitespace-nowrap text-xs text-gray-500 sm:text-sm dark:text-gray-300">
                {totalRows != null
                  ? `Showing ${displayStart} - ${displayEnd} of ${totalRows} items`
                  : `${displayStart} - ${displayEnd}`}
              </span>
              <select
                value={effectivePageSize}
                onChange={(e) => changePageSize(Number(e.target.value))}
                className={GRID.pageSizeSelect}
              >
                {pageSizeOptions.map((s) => (
                  <option key={s} value={s}>{s}{!isMobile && ' per page'}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center justify-center gap-1 sm:gap-2">
              {totalPages != null && (
                <button
                  type="button"
                  onClick={() => gotoPage(1)}
                  disabled={!canPrev}
                  className={cn('px-2 py-1.5 text-xs sm:py-1 sm:text-sm', GRID.pageButton)}
                >
                  {isMobile ? '«' : 'First'}
                </button>
              )}
              <button
                type="button"
                onClick={() => gotoPage(effectivePage - 1)}
                disabled={!canPrev}
                className={cn('p-1.5 sm:p-1', GRID.pageButton)}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="whitespace-nowrap px-2 py-1 text-xs text-gray-700 sm:px-3 sm:text-sm dark:text-gray-300">
                {totalPages != null ? `Page ${effectivePage} of ${totalPages}` : `Page ${effectivePage}`}
              </span>
              <button
                type="button"
                onClick={() => gotoPage(effectivePage + 1)}
                disabled={!canNext}
                className={cn('p-1.5 sm:p-1', GRID.pageButton)}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              {totalPages != null && (
                <button
                  type="button"
                  onClick={() => gotoPage(totalPages)}
                  disabled={!canNext}
                  className={cn('px-2 py-1.5 text-xs sm:py-1 sm:text-sm', GRID.pageButton)}
                >
                  {isMobile ? '»' : 'Last'}
                </button>
              )}
            </div>
          </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// FILTER CONTROLS
// ============================================================================

export function PillsFilter<T>({
  filter,
  selected,
  onChange,
}: {
  filter: StrictDataGridFilter<T>;
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const active = selected[0] ?? '';
  const pillOptions = filter.showAll === false
    ? filter.options
    : [{ value: '', label: filter.allLabel ?? 'All' }, ...filter.options];
  return (
    <div className={GRID.pillGroup} role="group" aria-label={filter.label}>
      {pillOptions.map((opt) => {
        const isActive = active === opt.value;
        const Icon = filter.icon;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value === '' ? [] : [opt.value])}
            className={cn(
              'inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
              isActive
                ? 'bg-blue-600 text-white'
                : 'text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700',
            )}
          >
            {Icon && opt.value === '' && <Icon className="h-3 w-3" />}
            {opt.color && !isActive && <span className={cn('h-1.5 w-1.5 rounded-full', opt.color)} />}
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export function DropdownFilter<T>({
  filter,
  selected,
  onChange,
}: {
  filter: StrictDataGridFilter<T>;
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const Icon = filter.icon;

  const toggle = (value: string) => {
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);
  };

  return (
    <GridDropdown
      isOpen={open}
      onClose={() => setOpen(false)}
      trigger={
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className={cn(
            GRID.controlButton,
            selected.length > 0 && 'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-600 dark:bg-blue-900/30 dark:text-blue-300',
          )}
        >
          {Icon && <Icon className="h-3.5 w-3.5" />}
          <span>{filter.label}</span>
          {selected.length > 0 && (
            <span className="min-w-[18px] rounded-full bg-blue-500 px-1 py-0.5 text-center text-[10px] font-semibold text-white">
              {selected.length}
            </span>
          )}
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
      }
    >
      <div className="max-h-64 overflow-y-auto">
        <div className="flex items-center justify-between border-b border-gray-100 p-2 dark:border-gray-700">
          <span className="text-xs text-gray-500 dark:text-gray-400">{filter.label}</span>
          <button type="button" onClick={() => onChange([])} className="text-xs text-gray-500 hover:underline dark:text-gray-400">
            Clear
          </button>
        </div>
        {filter.options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            onClick={() => toggle(opt.value)}
            className="flex w-full items-center justify-between px-3 py-1.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <span className="flex items-center gap-2">
              {opt.color && <span className={cn('h-2 w-2 rounded-full', opt.color)} />}
              <span className="text-[13px] text-gray-700 dark:text-gray-300">{opt.label}</span>
            </span>
            {selected.includes(opt.value) && <Check className="h-4 w-4 text-blue-500" />}
          </button>
        ))}
        {filter.options.length === 0 && (
          <div className="px-3 py-4 text-center text-xs text-gray-400">No options</div>
        )}
      </div>
    </GridDropdown>
  );
}

function RowActionsMenu({ actions }: { actions: StrictGridRowAction[] }) {
  const [open, setOpen] = useState(false);
  return (
    <GridDropdown
      isOpen={open}
      onClose={() => setOpen(false)}
      align="right"
      width="w-44"
      trigger={
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
          className="rounded p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
          aria-label="Row actions"
        >
          <MoreHorizontal className="h-4 w-4" />
        </button>
      }
    >
      <div className="p-1">
        {actions.map((a) => {
          const AIcon = a.icon;
          return (
            <button
              key={a.id}
              type="button"
              disabled={a.disabled}
              onClick={(e) => { e.stopPropagation(); setOpen(false); a.onClick(); }}
              className={cn(
                'flex w-full items-center gap-2 rounded px-2.5 py-1.5 text-left text-xs transition-colors disabled:opacity-40',
                a.danger
                  ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30'
                  : 'text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700',
              )}
            >
              {AIcon && <AIcon className="h-3.5 w-3.5" />}
              {a.label}
            </button>
          );
        })}
      </div>
    </GridDropdown>
  );
}
